import { TransactionSourceType } from '@pflegenavi/shared/api';
import { useTransactionApi } from '@pflegenavi/frontend/api-nursing-home';
import { useCallback } from 'react';
import { isDefined } from '@pflegenavi/shared/utils';
import * as Sentry from '@sentry/react';

/**
 * Limit concurrent requests to avoid browser connection exhaustion.
 * Browsers typically allow ~6 concurrent connections per domain.
 */
const MAX_CONCURRENT_FETCHES = 4;

/**
 * Execute async operations with limited concurrency to prevent connection exhaustion.
 */
async function throttledMap<T, R>(
  items: T[],
  fn: (item: T) => Promise<R>,
  concurrency: number
): Promise<R[]> {
  const results: R[] = [];
  let index = 0;

  async function worker(): Promise<void> {
    while (index < items.length) {
      const currentIndex = index++;
      results[currentIndex] = await fn(items[currentIndex]);
    }
  }

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    () => worker()
  );
  await Promise.all(workers);
  return results;
}

export interface FileInfo {
  uri: string;
  data: Promise<Blob>;
  fileType: string | null;
  isPdf: boolean;
  isImage: boolean;
}

/**
 * Minimal transaction shape required by getImages.
 * Exported so callers can build type-safe adapters for custom data sources.
 */
export interface TransactionForReceiptImages {
  id: string;
  amountInCent: number;
  additionalFeeAmountInCent: number;
  includedFeeAmountInCent: number;
  additionalFeesDivertedToPflegenavi: boolean;
  receiptImageIds?: string[];
  receiptDate?: Date;
  receiptType?: string;
  serviceProvider?: string;
  date: Date;
  sourceType: TransactionSourceType;
  cancelled: boolean;
  sequenceNumber: number;
  sequenceID?: string;
}

export interface ReceiptAttachmentTransactionData {
  id: string;
  amount: number;
  additionalFeeAmount: number;
  includedFeeAmount: number;
  additionalFeesDivertedToPflegenavi: boolean;
  receiptDate?: Date;
  receiptType?: string;
  serviceProvider?: string;
  cancelled: boolean; // Is this transaction itself cancelled?
  cancellation: boolean; // Is this transaction the cancellation of (=reversing) another transaction?
  cancellationDate?: Date;
  sequenceID?: string;
}

export interface ReceiptAttachment {
  image: FileInfo;
  receiptNumber: number;
  count: number;
  sequenceNumber: number;
  receiptImagesLength: number;
  transactionData: ReceiptAttachmentTransactionData;
}

async function fetchFile(fileUrl: string): Promise<FileInfo> {
  const response = await fetch(fileUrl);
  const extractedFileType = response.headers.get('content-type');

  const isPdf = extractedFileType === 'application/pdf';
  const isImage = extractedFileType?.startsWith('image/') ?? false;

  if (!isPdf && !isImage) {
    // eslint-disable-next-line no-console
    console.warn(
      `Fetched file with content-type ${extractedFileType} is not supported: ${fileUrl}`
    );
    Sentry.captureMessage(
      `Fetched file with content-type ${extractedFileType} is not supported: ${fileUrl}`,
      'warning'
    );
  }

  return {
    uri: fileUrl,
    data: response.blob(),
    fileType: extractedFileType,
    isPdf: isPdf,
    isImage: isImage,
  };
}

export async function getImages(
  transactions: TransactionForReceiptImages[],
  getUris: (
    imageIds: string[]
  ) => Promise<Record<string, string | undefined> | undefined>,
  type: 'pdf' | 'image',
  fetchFile: (fileUrl: string) => Promise<FileInfo>
): Promise<ReceiptAttachment[]> {
  // Retrieve all receipt ids
  const receiptIds = transactions
    .flatMap((transaction) => transaction.receiptImageIds)
    .filter(isDefined);

  // Get map between receipt id and receipt url
  const receiptImagesObject = await getUris(receiptIds);

  // Fetch files with throttling to prevent browser connection exhaustion
  const entries = Object.entries(receiptImagesObject ?? {});
  const asEntries = await throttledMap(
    entries,
    async ([key, value]): Promise<[string, FileInfo | undefined]> => {
      if (!value) {
        return [key, undefined];
      }
      const fileInfo: FileInfo = await fetchFile(value);
      if (type === 'pdf' && fileInfo.isPdf) {
        return [key, fileInfo];
      } else if (type === 'image' && fileInfo.isImage) {
        return [key, fileInfo];
      } else {
        return [key, undefined];
      }
    },
    MAX_CONCURRENT_FETCHES
  );

  // And recreate the map
  const receiptIdToFileInfo = Object.fromEntries(asEntries);

  // Compute final data
  return transactions.flatMap((transaction, index) => {
    const receiptNumber = index + 1;

    const fileInfos = (transaction.receiptImageIds ?? [])
      .map((receiptImageId) => {
        return receiptIdToFileInfo[receiptImageId];
      })
      .filter(isDefined);

    const receiptImagesLength = fileInfos.length ?? 0;
    return fileInfos.map((fileInfo, count) => {
      const isCancellation =
        transaction.sourceType === TransactionSourceType.CancelledReceipt;
      return {
        image: fileInfo,
        receiptNumber,
        count: count + 1,
        sequenceNumber: transaction.sequenceNumber,
        receiptImagesLength,
        transactionData: {
          id: transaction.id,
          amount: transaction.amountInCent,
          additionalFeeAmount: transaction.additionalFeeAmountInCent,
          includedFeeAmount: transaction.includedFeeAmountInCent,
          additionalFeesDivertedToPflegenavi:
            transaction.additionalFeesDivertedToPflegenavi,
          receiptDate: transaction.receiptDate,
          serviceProvider: transaction.serviceProvider,
          receiptType: transaction.receiptType,
          cancelled: transaction.cancelled,
          cancellation: isCancellation,
          cancellationDate: isCancellation ? transaction.date : undefined,
          sequenceID: transaction.sequenceID,
        },
      };
    });
  });
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useReceiptImagesForPdf = () => {
  const api = useTransactionApi();

  const getImagesFromReceiptIds = useCallback(
    async (transactions: TransactionForReceiptImages[]) => {
      return await getImages(
        transactions,
        api.getReceiptImageUrls.bind(api),
        'image',
        fetchFile
      );
    },
    [api]
  );

  const getPdfsFromReceiptIds = useCallback(
    async (transactions: TransactionForReceiptImages[]) => {
      return await getImages(
        transactions,
        api.getReceiptImageUrls.bind(api),
        'pdf',
        fetchFile
      );
    },
    [api]
  );

  return {
    getImagesFromReceiptIds,
    getPdfsFromReceiptIds,
  };
};
